import { ReactElement, useEffect } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { NavLink, useLocation } from 'react-router-dom'
import { useQuery, NetworkStatus } from '@apollo/client'
import { isEmpty } from 'lodash'

import { Button } from 'components/v2/Button'
import EmptyState from 'components/v2/EmptyState'
import Heading from 'components/v2/Heading'
import { Loader } from '@10xLabs/react-web/components/Loader'

import { Content, Header } from 'components/Tabs'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { SubcampaignTabs } from 'features/Subcampaigns/components/Tabs'
import { getQueries } from 'utils/route'
import { canReadIncidenceSubcampaigns } from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'
import { GET_INCIDENCE_SUBCAMPAIGNS } from 'features/Campaigns/queries-legacy'
import { GTIncidenceSubcampaignsInput, GTIncidenceSubcampaignsResponse, GTPagination } from 'gql/types.generated'
import { SubcampaignsList as SubcampaignList } from './List'
import { SubcampaignsPagination } from '../../components/Pagination'
import { SubcampaignFilters } from 'features/Subcampaigns/components/Filters'
import { ContentBody } from 'components/v2/Content'
import { usePlace } from 'features/Places/hooks'

const LIMIT = 30

const messages = defineMessages({
  helmet: {
    id: 'subcampaigns.incidences.helmet',
    defaultMessage: 'Incidences Subcampaigns | Nexbus Admin'
  }
})

const SubcampaignsView = (): ReactElement => {
  const location = useLocation()
  let content
  const intl = useIntl()
  const login = useLogin()
  const hasAccess = canReadIncidenceSubcampaigns(login.accessToken.policies)
  const searchQuery = getQueries(location.search) as Record<string, string>
  const { campaignId, origin, destination } = searchQuery
  const page = parseInt(searchQuery.page || '1')

  const variables: { input: GTIncidenceSubcampaignsInput; pagination: GTPagination } = {
    input: {
      campaignID: campaignId,
      tripOriginID: origin,
      tripDestinationID: destination
    },
    pagination: {
      size: LIMIT,
      page: page - 1
    }
  }

  const { loading, error, data, refetch, networkStatus } = useQuery<{
    incidenceSubcampaigns: GTIncidenceSubcampaignsResponse
  }>(GET_INCIDENCE_SUBCAMPAIGNS, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables,
    skip: !hasAccess
  })

  const originPlace = usePlace(origin, { skip: !origin })
  const destinationPlace = usePlace(destination, { skip: !destination })

  const filters = {
    origin: originPlace || { id: '', name: '' },
    destination: destinationPlace || { id: '', name: '' }
  }
  useEffect(() => {
    document.title = intl.formatMessage(messages.helmet)
    hasAccess && refetch()
  }, [])

  if (!hasAccess) {
    return <ForbiddenView feature={FEATURES.CAMPAIGN} />
  }

  if (networkStatus === NetworkStatus.refetch || loading) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (error) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="subcampaigns.error.description"
            defaultMessage="There was an error while loading your subcampaigns"
          />
        </EmptyState.Description>

        <EmptyState.Action>
          <Button
            onClick={(): void => {
              refetch()
            }}
            kind="primary"
          >
            <FormattedMessage id="subcampaigns.error.action" defaultMessage="Reload your subcampaigns" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(data?.incidenceSubcampaigns.items) && page === 1) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage id="subcampaigns.empty.description" defaultMessage={"You don't have any subcampaign yet"} />
        </EmptyState.Description>
      </EmptyState.Container>
    )
  } else if (isEmpty(data?.incidenceSubcampaigns.items)) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="subcampaign.empty.filters.description"
            defaultMessage="There was no subcampaign found according to your filters"
          />
        </EmptyState.Description>

        <EmptyState.Action>
          <Button as={NavLink} to="/subcampaigns/incidences" kind="primary">
            <FormattedMessage id="subcampaign.empty.filters.action" defaultMessage="Reset filters" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else {
    content = (
      <>
        <SubcampaignList subcampaigns={data?.incidenceSubcampaigns.items ?? []} />
        <SubcampaignsPagination
          page={page}
          limit={LIMIT}
          totalItems={data?.incidenceSubcampaigns.total ?? 0}
          currentItemsCount={data?.incidenceSubcampaigns.items.length ?? 0}
          campaignId={campaignId}
        />
      </>
    )
  }

  return (
    <>
      <Header>
        <Heading.H3>
          <FormattedMessage id="subcampaigns.header.label" defaultMessage="Subcampaigns" />
        </Heading.H3>
      </Header>
      <SubcampaignTabs />
      <Content>
        <ContentBody>
          <SubcampaignFilters filters={filters} />
          {content}
        </ContentBody>
      </Content>
    </>
  )
}

export default withLastUrl(SubcampaignsView)
